import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Handles the Write Review form submission via backend (with Turnstile + Reviews.io API key)
export default class extends Controller {
  static targets = [
    "modal", "form", "nameInput", "emailInput", "ratingInput", "reviewInput",
    "starRating", "submitButton", "submitText", "submitSpinner",
    "successMessage", "errorMessage", "errorText", "turnstileContainer"
  ]

  static values = {
    sku: String,
    submitUrl: String
  }

  connect() {
    // Connection guard for Turbo compatibility
    if (this.element.dataset.reviewFormConnected === 'true') return
    this.element.dataset.reviewFormConnected = 'true'

    // Skip expensive operations during Turbo preview
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.selectedRating = 0
    this.modal = null
  }

  disconnect() {
    // Clean up Modal instance
    if (this.modal) {
      this.modal.dispose()
      this.modal = null
    }
    delete this.element.dataset.reviewFormConnected
  }

  // Open the write review modal using Bootstrap's API (safer with Turbo)
  openModal() {
    if (this.hasModalTarget) {
      this.modal = Modal.getOrCreateInstance(this.modalTarget)
      this.modal.show()
    }
  }

  setRating(event) {
    event.preventDefault()
    const rating = parseInt(event.currentTarget.dataset.rating, 10)
    this.selectedRating = rating
    this.ratingInputTarget.value = rating
    this.updateStarDisplay()
  }

  updateStarDisplay() {
    const buttons = this.starRatingTarget.querySelectorAll('.star-btn')
    buttons.forEach((btn, index) => {
      const starIndex = index + 1
      if (starIndex <= this.selectedRating) {
        btn.style.color = '#577d3c'
      } else {
        btn.style.color = '#ddd'
      }
    })
  }

  async submit(event) {
    event.preventDefault()

    // Validate rating
    if (!this.selectedRating || this.selectedRating < 1) {
      this.showError('Please select a star rating.')
      return
    }

    // Validate form
    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity()
      return
    }

    // Get Turnstile token
    const turnstileToken = this.getTurnstileToken()
    if (!turnstileToken) {
      this.showError('Please complete the security verification.')
      return
    }

    // Show loading state
    this.setLoading(true)
    this.hideMessages()

    try {
      const response = await this.submitReview(turnstileToken)
      const data = await response.json().catch(() => ({}))

      if (response.ok && data.success) {
        this.showSuccess()
        this.resetForm()
      } else {
        const errorMsg = data.error || data.message || 'Failed to submit review. Please try again.'
        this.showError(errorMsg)
        this.resetTurnstile()
      }
    } catch (error) {
      console.error('[review-form] Submission error', error)
      this.showError('Network error. Please check your connection and try again.')
      this.resetTurnstile()
    } finally {
      this.setLoading(false)
    }
  }

  async submitReview(turnstileToken) {
    const body = {
      sku: this.skuValue,
      name: this.nameInputTarget.value.trim(),
      email: this.emailInputTarget.value.trim(),
      rating: this.selectedRating.toString(),
      review: this.reviewInputTarget.value.trim(),
      'cf-turnstile-response': turnstileToken
    }

    return fetch(this.submitUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    })
  }

  getTurnstileToken() {
    // Try to find the Turnstile response input
    const turnstileInput = this.element.querySelector('[name="cf-turnstile-response"]')
    return turnstileInput?.value || null
  }

  resetTurnstile() {
    // Reset Turnstile widget if available
    if (typeof turnstile !== 'undefined' && this.hasTurnstileContainerTarget) {
      const widget = this.turnstileContainerTarget.querySelector('.cf-turnstile')
      if (widget) {
        try {
          turnstile.reset(widget)
        } catch (e) {
          // Turnstile might not be fully loaded yet
          console.debug('[review-form] Turnstile reset skipped', e)
        }
      }
    }
  }

  setLoading(isLoading) {
    this.submitButtonTarget.disabled = isLoading

    if (isLoading) {
      this.submitTextTarget.classList.add('d-none')
      this.submitSpinnerTarget.classList.remove('d-none')
    } else {
      this.submitTextTarget.classList.remove('d-none')
      this.submitSpinnerTarget.classList.add('d-none')
    }
  }

  showSuccess() {
    this.successMessageTarget.classList.remove('d-none')
    this.formTarget.classList.add('d-none')
  }

  showError(message) {
    this.errorTextTarget.textContent = message
    this.errorMessageTarget.classList.remove('d-none')
  }

  hideMessages() {
    this.successMessageTarget.classList.add('d-none')
    this.errorMessageTarget.classList.add('d-none')
  }

  resetForm() {
    this.formTarget.reset()
    this.selectedRating = 0
    this.ratingInputTarget.value = ''
    this.updateStarDisplay()
    this.resetTurnstile()
  }

  // Reset form when modal is closed (to allow submitting another review)
  resetOnClose() {
    this.hideMessages()
    this.formTarget.classList.remove('d-none')
    this.resetForm()
  }
}
